/**
 * ChatInterface - displays messages and handles chat interaction
 * Supports user and AI messages with typing indicator
 * Includes pending message for optimistic updates
 * Renders AI messages with Markdown + inline citation badges
 */
import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faComments, faUser, faRobot, faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import styles from './ChatInterface.module.css';
import type { Message, Source } from '@/types/elections';

interface ChatInterfaceProps {
    messages: Message[];
    isAiTyping?: boolean;
    pendingMessage?: string | null;
    isLoading?: boolean;
    emptyStateContent?: React.ReactNode;
    onSuggestionClick?: (suggestion: string) => void;
}

function ChatSkeleton() {
    return (
        <div className={styles.skeleton}>
            <div className={styles.skeletonMessage}>
                <div className={styles.skeletonAvatar} />
                <div className={styles.skeletonBubble} />
            </div>
            <div className={`${styles.skeletonMessage} ${styles.skeletonRight}`}>
                <div className={styles.skeletonBubbleShort} />
                <div className={styles.skeletonAvatar} />
            </div>
            <div className={styles.skeletonMessage}>
                <div className={styles.skeletonAvatar} />
                <div className={styles.skeletonBubbleLong} />
            </div>
        </div>
    );
}

const SUGGESTIONS = [
    "¿Dónde estudió Cesar Acuña?",
    "Historial político de Keiko Fujimori",
    "¿Qué antecedentes tiene Rafael López Aliaga?",
];

/**
 * Cleans AI response content and transforms inline [N] citations into <cite> tags.
 * - Strips tag-name citations like [HOJA DE VIDA], [PLAN DE GOBIERNO]
 * - Removes trailing "Referencia:" lines (sources are rendered separately)
 * - Converts [1] -> <cite> for inline badge rendering
 */
function processContentWithCitations(content: string): string {
    let cleaned = content;

    // Remove tag-name citations the AI might still produce
    cleaned = cleaned.replace(/\[(?:HOJA DE VIDA|PLAN DE GOBIERNO|PERFIL CANDIDATO|REGISTRO CANDIDATOS|INFORMACION ELECTORAL|DATOS DE ENCUESTAS|RESPUESTA FRECUENTE)(?:\s*-\s*[^\]]+)?\]/gi, '');

    // Remove trailing "Referencia:" or "Fuentes:" lines with citation numbers
    cleaned = cleaned.replace(/\n\n(?:Referencia|Fuentes?|Referencias?):\s*(?:\[\d+\]\s*)+$/i, '');

    // Clean up double spaces left by removals
    cleaned = cleaned.replace(/ {2,}/g, ' ').trim();

    // Convert [1] -> <cite> tags
    return cleaned.replace(/\[(\d+)\]/g, '<cite data-ref="$1">$1</cite>');
}

/**
 * Renders a single source reference chip with expandable snippet
 */
function SourceChip({ source, index }: { source: Source; index: number }) {
    const [expanded, setExpanded] = useState(false);
    const number = index + 1;

    // Clean the title for display
    const displayTitle = source.title || 'Fuente desconocida';

    return (
        <div className={styles.sourceChip}>
            <button
                className={styles.sourceChipButton}
                onClick={() => setExpanded(!expanded)}
                aria-expanded={expanded}
            >
                <span className={styles.sourceNumber}>{number}</span>
                <span className={styles.sourceTitle}>{displayTitle}</span>
                {source.text && (
                    <FontAwesomeIcon
                        icon={expanded ? faChevronUp : faChevronDown}
                        className={styles.sourceToggle}
                    />
                )}
            </button>
            {expanded && source.text && (
                <div className={styles.sourceSnippet}>
                    <p>{source.text}</p>
                </div>
            )}
        </div>
    );
}

/**
 * Renders a full message bubble with markdown content and source references
 */
function AssistantMessage({ message }: { message: Message }) {
    const processedContent = useMemo(
        () => processContentWithCitations(message.content),
        [message.content]
    );

    // Custom components for ReactMarkdown to handle <cite> tags
    const markdownComponents = useMemo(() => ({
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        cite: ({ children, ...props }: any) => {
            const ref = props['data-ref'];
            return (
                <span className={styles.citationBadge} title={`Fuente ${ref}`}>
                    {children}
                </span>
            );
        },
    }), []);

    return (
        <div className={styles.content}>
            <div className={styles.bubble}>
                <div className={styles.markdown}>
                    <ReactMarkdown
                        rehypePlugins={[rehypeRaw]}
                        components={markdownComponents}
                    >
                        {processedContent}
                    </ReactMarkdown>
                </div>
            </div>
            {message.sources && message.sources.length > 0 && (
                <div className={styles.sources}>
                    <span className={styles.sourcesLabel}>Fuentes</span>
                    <div className={styles.sourcesList}>
                        {message.sources.map((source, idx) => (
                            <SourceChip key={idx} source={source} index={idx} />
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

export function ChatInterface({
    messages,
    isAiTyping,
    pendingMessage,
    isLoading,
    emptyStateContent,
    onSuggestionClick
}: ChatInterfaceProps) {
    const messagesEndRef = useRef<HTMLDivElement>(null);

    const scrollToBottom = useCallback(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, []);

    useEffect(() => {
        scrollToBottom();
    }, [messages, isAiTyping, pendingMessage, scrollToBottom]);

    const hasContent = messages.length > 0 || pendingMessage || isAiTyping;

    if (isLoading) {
        return <ChatSkeleton />;
    }

    if (!hasContent) {
        return (
            <div className={styles.emptyState}>
                <div className={styles.emptyIcon}>
                    <FontAwesomeIcon icon={faComments} />
                </div>
                <h3 className={styles.emptyTitle}>Perú IA</h3>
                <p className={styles.emptyText}>
                    Puedes preguntarme sobre propuestas, experiencia, historial político
                    y más de cualquier candidato presidencial.
                </p>
                {emptyStateContent}
            </div>
        );
    }

    return (
        <div className={styles.container}>
            {messages.map((message) => (
                <div
                    key={message.id}
                    className={`${styles.message} ${styles[message.role]}`}
                >
                    <div className={styles.avatar}>
                        <FontAwesomeIcon icon={message.role === 'user' ? faUser : faRobot} />
                    </div>
                    {message.role === 'assistant' ? (
                        <AssistantMessage message={message} />
                    ) : (
                        <div className={styles.content}>
                            <div className={styles.bubble}>
                                {message.content}
                            </div>
                        </div>
                    )}
                </div>
            ))}

            {pendingMessage && (
                <div className={`${styles.message} ${styles.user}`}>
                    <div className={styles.avatar}>
                        <FontAwesomeIcon icon={faUser} />
                    </div>
                    <div className={styles.content}>
                        <div className={styles.bubble}>
                            {pendingMessage}
                        </div>
                    </div>
                </div>
            )}

            {isAiTyping && (
                <div className={`${styles.message} ${styles.assistant}`}>
                    <div className={styles.avatar}>
                        <FontAwesomeIcon icon={faRobot} />
                    </div>
                    <div className={styles.content}>
                        <div className={`${styles.bubble} ${styles.typing}`}>
                            <span className={styles.typingDot}></span>
                            <span className={styles.typingDot}></span>
                            <span className={styles.typingDot}></span>
                        </div>
                    </div>
                </div>
            )}

            {!pendingMessage && !isAiTyping && messages.length > 0 && messages[messages.length - 1].role === 'assistant' && (
                <div className={styles.suggestions}>
                    <ul className={styles.suggestionList}>
                        {SUGGESTIONS.map((suggestion, index) => (
                            <li
                                key={index}
                                className={styles.suggestionChip}
                                onClick={() => onSuggestionClick?.(suggestion)}
                            >
                                {suggestion}
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            <div ref={messagesEndRef} />
        </div>
    );
}
